import React, { useState, useEffect } from 'react';
import { Fab, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { formatRoute } from 'react-router-named-routes';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import TopNavBar from '../user/top_nav_bar';
import { END_USER_RECIPE_SINGLE_COLLECTION_ROUTE } from '../../services/routes';
import MealBasketWidget from '../../features/end_user_shopping/components/meal_basket_widget';
import RecipeCollectionsNewBoardModal from './recipe_collections_new_board_modal';
import {
  useCreateSmorgBoardMutation,
  useGetSharedBoardsByIDsQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  myFavouritesRecipesBoard,
  recipesBoardFirstMealID,
  recipesBoardsForCollectionsScreenComparer,
} from '../../services/smorg_board';
import RecipeCollectionItem from './recipe_collection_item';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetEndUserSpaceQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  trackCollectionItemClick,
  useTracker,
} from '../../operations/tracking_operations';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';
import { createEndUserFavouritesRecipesBoard } from '../../operations/recipes_operations';

const RecipeCollectionsView = () => {
  const navigate = useNavigate();
  const tracker = useTracker();

  const { data: userProfile } = useGetUserProfileQuery();

  useEffect(() => {
    console.log(
      `useEffect tracking Collections screen clicked, tracker = ${tracker}`,
    );
    tracker(userProfile, ['Collections screen clicked']);
    // tracker() will mutate userProfile, causing a render loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracker, !!userProfile]);

  const { data: space, isLoading: isSpaceLoading } = useGetEndUserSpaceQuery();

  const spaceAssignedSharedBoardIDs = space?.sharedRecipesBoardIDs || [];

  const { data: spaceAssignedSharedBoards } = useGetSharedBoardsByIDsQuery(
    spaceAssignedSharedBoardIDs,
    {
      skip:
        isSpaceLoading ||
        !spaceAssignedSharedBoardIDs ||
        spaceAssignedSharedBoardIDs.length === 0,
      refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
    },
  );

  const { data: ownedRecipesBoards, isLoading: isOwnedRecipesLoading } = useGetOwnedRecipesBoardsQuery();

  const myFavouritesBoardID = myFavouritesRecipesBoard(ownedRecipesBoards)?.id;

  const boards = [
    ...(ownedRecipesBoards || []),
    ...(spaceAssignedSharedBoards || []),
  ].sort(recipesBoardsForCollectionsScreenComparer);

  const [newBoardModalVisible, setNewBoardModalVisible] = useState(false);

  const onClickAddBoard = () => {
    setNewBoardModalVisible(true);
  };

  const onDismissNewBoardModal = () => {
    setNewBoardModalVisible(false);
  };

  const mealsExistInBasket = useSelector(
    (state) => state.endUserShopping.mealBasketItems.length > 0,
  );

  let newCollectionFabBottomPx = 67;
  if (mealsExistInBasket) {
    newCollectionFabBottomPx += 50;
  }

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const [createSmorgBoard] = useCreateSmorgBoardMutation();

  useEffect(() => {
    if (
      !isSpaceLoading &&
      !isOwnedRecipesLoading &&
      !!currentSpaceMembership &&
      !!ownedRecipesBoards &&
      ownedRecipesBoards.length === 0
    ) {
      createEndUserFavouritesRecipesBoard(
        currentSpaceMembership.id,
        createSmorgBoard,
      );
    }
  }, [
    createSmorgBoard,
    currentSpaceMembership,
    isOwnedRecipesLoading,
    isSpaceLoading,
    ownedRecipesBoards,
  ]);

  return (
    <>
      <TopNavBar />
      <MealBasketWidget />
      <div className="shared-programmes-view">
        <div className="alternatives-page">
          <div className="topNavBarMobile">
            <div className="screen-title" style={{ flexGrow: 1 }}>
              Collections
            </div>
          </div>
          <div
            style={{
              marginTop: '20px',
            }}>
            <div style={{ paddingLeft: '23px' }}>
              <Grid container>
                <div style={{ height: '30px' }}>&nbsp;</div>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    {boards.map((board) => {
                      const boardFirstMealID =
                        board.id !== myFavouritesBoardID &&
                        recipesBoardFirstMealID(board);
                      return (
                        <Grid item key={board.id} xs={6} sm={4} md={3} lg={2}>
                          <RecipeCollectionItem
                            boardID={board.id}
                            title={board.title}
                            firstMealID={boardFirstMealID}
                            onClickBoard={(boardID) => {
                              trackCollectionItemClick(
                                tracker,
                                userProfile,
                                board.id,
                                board.title,
                              );
                              navigate(
                                formatRoute(
                                  END_USER_RECIPE_SINGLE_COLLECTION_ROUTE,
                                  {
                                    boardID,
                                  },
                                ),
                              );
                            }}
                            isFavourite={board.id === myFavouritesBoardID}
                            isSharedBoard={(
                              spaceAssignedSharedBoardIDs || []
                            ).includes(board.id)}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </div>
          {boards.length === 0 && (
            <div
              style={{
                height: 'var(--app-height)',
                width: '100%',
                display: 'flex',
              }}>
              <div style={{ margin: 'auto' }}>Nothing here yet</div>
            </div>
          )}
        </div>
      </div>
      <Fab
        color="warning"
        aria-label="add"
        size="small"
        style={{
          position: 'fixed',
          bottom: `${newCollectionFabBottomPx}px`,
          right: '10px',
          color: 'var(--app-primary-font-color)',
        }}
        onClick={onClickAddBoard}>
        <AddIcon />
      </Fab>
      {newBoardModalVisible && (
        <RecipeCollectionsNewBoardModal onDismiss={onDismissNewBoardModal} />
      )}
    </>
  );
};

export default RecipeCollectionsView;
